/**
 * Hash routes. The route owns every piece of meaningful state, so refresh,
 * browser Back and folding the phone never lose context.
 *
 *   #/home
 *   #/spend                     latest published month
 *   #/spend/2026-08             a month (2026-10 = the open plan)
 *   #/spend/2026-08/Dining      a month with a category's evidence open
 *   #/wealth
 *   #/insights/2026-08/changed  month + answered question
 *   #/close
 *   #/report/2026-09            the close pack for a sealed month (printable)
 */
export const SECTIONS = ['home', 'spend', 'wealth', 'insights'] as const;
export type Section = (typeof SECTIONS)[number];

export type Route =
  | { section: 'home' }
  | { section: 'spend'; month?: string; category?: string }
  | { section: 'wealth' }
  | { section: 'insights'; month?: string; question?: string }
  | { section: 'close' }
  | { section: 'report'; month?: string };

const MONTH = /^\d{4}-\d{2}$/;

export function parseRoute(hash: string): Route {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean).map((p) => {
    try { return decodeURIComponent(p); } catch { return p; }
  });
  const [section, a, b] = parts;
  const month = a && MONTH.test(a) ? a : undefined;
  switch (section) {
    case 'spend':
      return { section: 'spend', month, category: month ? b : undefined };
    case 'insights':
      return { section: 'insights', month, question: month ? b : undefined };
    case 'wealth':
      return { section: 'wealth' };
    case 'close':
      return { section: 'close' };
    case 'report':
      return { section: 'report', month };
    default:
      return { section: 'home' };
  }
}

export function formatRoute(route: Route): string {
  const seg = (s?: string) => (s ? `/${encodeURIComponent(s)}` : '');
  switch (route.section) {
    case 'spend':
      return `#/spend${seg(route.month)}${route.month ? seg(route.category) : ''}`;
    case 'insights':
      return `#/insights${seg(route.month)}${route.month ? seg(route.question) : ''}`;
    case 'report':
      return `#/report${seg(route.month)}`;
    default:
      return `#/${route.section}`;
  }
}

/** Push a new history entry (Back returns to the previous state). */
export function navigate(route: Route) {
  const next = formatRoute(route);
  if (window.location.hash !== next) window.location.hash = next;
}

/** Replace the current entry (for normalising, not for user navigation). */
export function replaceRoute(route: Route) {
  const url = `${window.location.pathname}${window.location.search}${formatRoute(route)}`;
  window.history.replaceState(window.history.state, '', url);
  window.dispatchEvent(new HashChangeEvent('hashchange'));
}
